import { type CSSProperties, type ReactNode } from 'react'
import clsx from 'clsx'
import type { ISODate } from '@app/shared'

import { localMinutesOfDay } from '../../lib/time'

import type { MailVerdict } from '../../lib/data/types'
import type { DayController, DayItem, Phase, Section } from './controller'
import {
  PILE_MOVE_TIP,
  PILE_NAME,
  PILE_ORDER,
  PILE_SEG,
  ageLabel,
  ageTone,
  assignmentWhy,
  clock12,
  currentPileTip,
  inboxName,
  mailPill,
  mailWhen,
  movedLine,
  pickedPhrase,
  senderLine,
  seenLine,
  sortedBy,
  type SnoozeChoice,
  type SourceChip,
  type Waiting,
} from './designed'
import { BellGlyph, CheckGlyph, DocGlyph, Dots, External, MailGlyph, MoonGlyph, Shield, Sparkle, XGlyph } from './icons'
import { RulesMenu, SnoozeMenu } from './menus'
import { Kc, Pill, SrcLine, Tip } from './parts'
import { proposedCount } from './proposals'
import { StepsPanel } from './steps'

// ============================================================================
// THE CARD (Main + Components boards) — mail, a bCourses assignment, or a reminder.
//
// Resting: kind tile, title, the why, the source line. Hover: the source-coloured edge, the tint,
// and the actions (Done · Snooze · Dismiss · Open, the 1–4 pile control, the ⋯ rules). Keyboard
// focus: the same edge plus the ring, and a key line where the buttons would be.
// ============================================================================

export interface DayCtx {
  ctrl: DayController
  today: ISODate
  nowMinutes: number
  /** Each mail account's freshness, by account id — a card from a stale inbox says so. */
  chips: Map<string, SourceChip>
  pick: ISODate
  setPick: (d: ISODate) => void
  onRule: (item: DayItem, verdict: MailVerdict) => void
}

/** Where it came from, as the hover edge (README → Motion): violet mail, blue bCourses, orange reminders, amber security. */
function edgeOf(item: DayItem): string {
  if (item.kind === 'assignment') return 'var(--color-school)'
  if (item.kind === 'reminder') return 'var(--color-tasks)'
  if (item.mail?.security) return 'var(--color-warning)'
  return 'var(--color-chat)'
}

export function openItem(item: DayItem) {
  if (item.url) window.open(item.url, '_blank', 'noopener,noreferrer')
}

function KindTile({ item, unsorted, phase, mark }: { item: DayItem; unsorted?: boolean; phase: Phase; mark?: string }) {
  const done = mark === 'done' && phase !== 'rest'
  return (
    <span
      className={clsx(
        'day-kind mt-px flex size-7 flex-none items-center justify-center rounded-[9px]',
        unsorted ? 'box-border border border-dashed border-line-2 bg-transparent text-muted' : 'bg-fill-2',
        !unsorted && item.kind === 'mail' && (item.mail?.security ? 'text-warning' : 'text-ink-3'),
        item.kind === 'assignment' && 'text-school',
        item.kind === 'reminder' && 'text-tasks',
      )}
    >
      {done ? (
        <CheckGlyph size={15} className="day-check" />
      ) : item.kind === 'assignment' ? (
        <DocGlyph size={15} />
      ) : item.kind === 'reminder' ? (
        <BellGlyph size={15} />
      ) : item.mail?.security ? (
        <Shield size={15} />
      ) : (
        <MailGlyph size={15} dashed={unsorted} />
      )}
    </span>
  )
}

/** One action button with its tooltip. */
function Act({
  icon,
  label,
  k,
  tip,
  onClick,
  href,
  expanded,
  tipAlign,
  badge,
}: {
  icon?: ReactNode
  label: string
  k: string
  tip: string
  onClick?: () => void
  href?: string
  expanded?: boolean
  tipAlign?: 'right'
  /** A count after the label (Next steps: proposals still waiting). */
  badge?: ReactNode
}) {
  const inner = (
    <>
      {icon}
      {label}
      {badge}
      {href && <External size={13} />}
      <Kc>{k}</Kc>
      <Tip name={label} k={k} align={tipAlign}>
        {tip}
      </Tip>
    </>
  )
  if (href) {
    return (
      <a className="day-btn day-tipw" href={href} target="_blank" rel="noreferrer">
        {inner}
      </a>
    )
  }
  return (
    <button type="button" className="day-btn day-tipw" onClick={onClick} aria-expanded={expanded}>
      {inner}
    </button>
  )
}

/** The 1–4 control: where it is now, pressed; the other three move it. */
export function PileSegs({ verdict, item, onMove, className }: { verdict: MailVerdict | null; item: NonNullable<DayItem['mail']>; onMove: (v: MailVerdict) => void; className?: string }) {
  return (
    <div role="group" aria-label="Move to pile" className={clsx('flex items-center gap-[2px] rounded-button border border-line bg-fill-2 p-[2px]', className)}>
      {PILE_ORDER.map((v, i) => {
        const on = v === verdict
        return (
          <button key={v} type="button" className="day-seg day-tipw" aria-pressed={on} onClick={() => !on && onMove(v)}>
            <Kc>{i + 1}</Kc>
            {PILE_SEG[v]}
            <Tip name={on ? PILE_NAME[v] : `Move to ${PILE_NAME[v]}`} k={String(i + 1)} align="right">
              {on ? currentPileTip(item) : PILE_MOVE_TIP[v]}
            </Tip>
          </button>
        )
      })}
    </div>
  )
}

/** The key line that replaces the buttons on keyboard focus (Keyboard board). */
function keyLine(item: DayItem): string {
  if (item.kind === 'mail') return 'D done · S snooze · X dismiss · O open · 1–4 move · ? all keys'
  if (item.kind === 'reminder') return 'D done · ? all keys'
  return item.url ? 'O open · ? all keys' : '? all keys'
}

export function ItemCard({
  item,
  section,
  index,
  ctx,
  waiting,
  unsorted,
  enter,
}: {
  item: DayItem
  section: Section
  index: number
  ctx: DayCtx
  waiting?: Waiting
  unsorted?: boolean
  /** A row the owner asked to see ("1 new · show") shows its source edge as it arrives (14e). */
  enter?: boolean
}) {
  const { ctrl, today } = ctx
  const phased = ctrl.phaseOf(item.key, section)
  const phase: Phase = enter && phased.phase === 'rest' ? 'returning' : phased.phase
  const mark = phased.mark
  const menu = ctrl.menu?.key === item.key ? ctrl.menu : null
  const mail = item.mail
  const stepsOpen = item.kind === 'mail' && ctrl.steps === item.key

  // ── The title row's right-hand side ──
  let right: ReactNode = null
  if (waiting) {
    right = (
      <Pill tone={ageTone(waiting.ageDays) === 'orange' ? 'orange' : 'plain'} className="ml-auto">
        {ageLabel(waiting.ageDays)}
      </Pill>
    )
  } else if (mail) {
    const when = mailWhen(mail, today)
    const pill = mailPill(mail)
    if (when) right = <span className={clsx('ml-auto whitespace-nowrap text-[11.5px] font-medium', when.urgent ? 'text-urgent-time' : 'text-microcopy')}>{when.text}</span>
    else if (pill) right = <Pill tone={pill.tone === 'warn' ? 'warn' : 'quiet'} className="ml-auto">{pill.text}</Pill>
  } else if (item.kind === 'assignment' && item.assignment?.dueAt) {
    right = <span className="ml-auto whitespace-nowrap text-[11.5px] font-medium text-urgent-time">{clock12(item.assignment.dueAt)}</span>
  }

  // ── The why, and the source line ──
  let why: string | null = null
  let src: ReactNode[] = []
  let moved: ReactNode = null
  if (mail) {
    why = mail.reason
    const chip = ctx.chips.get(mail.accountId)
    const inbox = chip && chip.state === 'stale' ? <span className="text-warning">{`${inboxName(mail.accountAddress)} · ${chip.value}`}</span> : inboxName(mail.accountAddress)
    const line = movedLine(mail)
    const prov = sortedBy(mail, today)
    let provNode: ReactNode = prov ? (
      <span className="inline-flex items-center gap-1">
        <Sparkle size={11} className="text-chat" />
        {prov}
      </span>
    ) : null
    if (line && ctrl.movedNow.has(mail.id)) {
      moved = (
        <div className="mt-[2px] inline-flex items-center gap-[6px] self-start rounded-pill border border-accent-line bg-accent-fill px-[9px] py-[3px] text-[11px] text-accent">
          <span className="line-through opacity-75">{line.was}</span>
          <span>{line.now}</span>
        </div>
      )
      provNode = null
    } else if (line) {
      provNode = (
        <span>
          {line.was} <span className="text-accent">{line.now}</span>
        </span>
      )
    }
    src = [senderLine(mail), inbox, unsorted ? null : seenLine(mail, today), unsorted ? null : provNode]
    if (unsorted) src = [senderLine(mail), inbox, seenLine(mail, today)]
  } else if (waiting) {
    why = waiting.why
    src = waiting.source
  } else if (item.kind === 'assignment' && item.assignment) {
    why = assignmentWhy(item.assignment.dueAt ? localMinutesOfDay(item.assignment.dueAt) : null)
    src = [item.assignment.courseCode, 'bCourses']
  } else if (item.kind === 'reminder') {
    src = ['Reminders', 'due today']
  }

  const toggleMenu = (kind: 'snooze' | 'rules') => ctrl.setMenu(menu?.kind === kind || (kind === 'snooze' && menu?.kind === 'picker') ? null : { key: item.key, kind })
  const onPreset = (c: SnoozeChoice) => ctrl.snooze(item, section, index, c.until, presetPhrase(c))

  return (
    <div className="day-row" data-phase={phase} data-mark={mark}>
      <div className="day-clip">
        <article
          className={clsx(
            'day-item flex gap-3 border-t border-line-3 px-4',
            unsorted ? 'py-[10px]' : 'pb-[13px] pt-3',
            'last:rounded-b-card',
          )}
          style={{ '--day-src': edgeOf(item) } as CSSProperties}
          tabIndex={0}
          data-day-nav=""
          data-day-key={item.key}
          data-day-section={section}
          data-day-index={index}
          data-open={menu || stepsOpen ? 'true' : undefined}
          aria-label={item.title}
        >
          <KindTile item={item} unsorted={unsorted} phase={phase} mark={mark} />
          <div className={clsx('flex min-w-0 flex-1 flex-col', unsorted ? 'gap-[3px]' : 'gap-1')}>
            <div className={clsx('flex gap-[10px]', right && typeof right === 'object' && waiting ? 'items-center' : 'items-baseline')}>
              <h3 className={clsx('m-0 text-[12.5px] leading-[1.3] text-ink text-pretty', unsorted ? 'font-medium' : 'font-semibold')}>
                <span className="day-strike">{item.title}</span>
              </h3>
              {right}
            </div>
            {why && <p className="day-why m-0 text-[12px] leading-[1.45] text-ink-3 text-pretty">{why}</p>}
            <SrcLine parts={src} />
            {moved}
            <div className="day-keys text-[11px] text-microcopy">{keyLine(item)}</div>
            <Actions item={item} section={section} index={index} ctx={ctx} menu={menu?.kind ?? null} toggleMenu={toggleMenu} onPreset={onPreset} />
            {stepsOpen && <StepsPanel item={item} section={section} index={index} ctx={ctx} />}
          </div>
        </article>
      </div>
    </div>
  )
}

/** 'tonight, 8:00 PM' for the toast — the preset's own words, never re-derived. */
export function presetPhrase(c: SnoozeChoice): string {
  if (c.key === 1) return 'tonight, 8:00 PM'
  if (c.key === 2) return 'tomorrow, 8:00 AM'
  return c.detail
}

function Actions({
  item,
  section,
  index,
  ctx,
  menu,
  toggleMenu,
  onPreset,
}: {
  item: DayItem
  section: Section
  index: number
  ctx: DayCtx
  menu: 'snooze' | 'picker' | 'rules' | null
  toggleMenu: (k: 'snooze' | 'rules') => void
  onPreset: (c: SnoozeChoice) => void
}) {
  const { ctrl, today, nowMinutes } = ctx
  const mail = item.mail
  if (item.kind === 'reminder') {
    return (
      <div className="day-acts mt-2 flex-wrap items-center gap-[6px]">
        <Act icon={<CheckGlyph size={14} className="text-success" />} label="Done" k="D" tip="You dealt with it. It leaves the list; undo for 5 seconds." onClick={() => ctrl.finish(item, section, index, 'done')} />
      </div>
    )
  }
  if (item.kind === 'assignment') {
    if (!item.url) return null
    return (
      <div className="day-acts mt-2 flex-wrap items-center gap-[6px]">
        <Act label="Open in bCourses" k="O" tip="Opens it in a new tab. Nothing changes here." href={item.url} />
      </div>
    )
  }
  if (!mail) return null
  return (
    <div className="day-acts mt-2 flex-wrap items-center gap-[6px]">
      <Act icon={<CheckGlyph size={14} className="text-success" />} label="Done" k="D" tip="You dealt with it. It leaves the list; undo for 5 seconds." onClick={() => ctrl.finish(item, section, index, 'done')} />
      <span className="relative">
        <Act
          icon={<MoonGlyph size={14} />}
          label="Snooze"
          k="S"
          tip="Hide it until tonight, tomorrow morning, next week or a date you pick. It waits in Snoozed."
          onClick={() => toggleMenu('snooze')}
          expanded={menu === 'snooze' || menu === 'picker'}
        />
        <SnoozeMenu
          open={menu === 'snooze' || menu === 'picker'}
          picker={menu === 'picker'}
          today={today}
          nowMinutes={nowMinutes}
          target={{ kind: 'mail', receivedAt: mail.receivedAt }}
          pick={ctx.pick}
          onPick={ctx.setPick}
          onPreset={onPreset}
          onPicked={(until) => ctrl.snooze(item, section, index, until, pickedToastPhrase(until))}
          onOpenPicker={() => ctrl.setMenu({ key: item.key, kind: 'picker' })}
          onClose={() => ctrl.setMenu(null)}
        />
      </span>
      <Act icon={<XGlyph size={14} />} label="Dismiss" k="X" tip="Not relevant. It leaves for good; undo for 5 seconds." onClick={() => ctrl.finish(item, section, index, 'dismiss')} />
      <Act label="Open in Gmail" k="O" tip="Opens the thread in a new tab. Nothing changes here or in Gmail." href={mail.url} />
      <NextStepsButton item={item} ctx={ctx} />
      <PileSegs verdict={mail.verdict} item={mail} onMove={(v) => ctrl.move(item, section, index, v)} className="ml-auto" />
      <span className="relative">
        <button
          type="button"
          className="day-btn day-tipw w-[30px] justify-center p-0"
          aria-label="Rules for this sender"
          aria-expanded={menu === 'rules'}
          onClick={() => toggleMenu('rules')}
        >
          <Dots size={14} />
          <Tip name="Sender rules" k="R" align="right">
            Always important, or always noise, from {mail.from.address.toLowerCase()}.
          </Tip>
        </button>
        <RulesMenu open={menu === 'rules'} item={mail} onRule={(v) => ctx.onRule(item, v)} />
      </span>
    </div>
  )
}

/** Lane 6b: opens the Next steps panel under the card. The badge counts proposals still waiting. */
function NextStepsButton({ item, ctx }: { item: DayItem; ctx: DayCtx }) {
  const count = item.mail ? proposedCount(item.mail) : 0
  return (
    <Act
      icon={<Sparkle size={13} className="text-chat" />}
      label="Next steps"
      k="N"
      tip="What Claude proposes for this mail. Nothing happens until you accept one."
      onClick={() => ctx.ctrl.toggleSteps(item.key)}
      expanded={ctx.ctrl.steps === item.key}
      badge={
        count > 0 ? (
          <span className="rounded-pill bg-accent-fill px-[6px] text-[10.5px] font-semibold leading-[1.5] text-accent" aria-label={`${count} proposed`}>
            {count}
          </span>
        ) : null
      }
    />
  )
}

/** The toast's "Snoozed until Thu, Oct 8 at 4:26 PM" — the picker's own phrase. */
const pickedToastPhrase = pickedPhrase

/** The "Nothing else needs you today." line that is simply there when the last card leaves (14g). */
export function EmptyLine({ children }: { children: ReactNode }) {
  return <p className="m-0 border-t border-line-3 px-4 pb-[15px] pt-[13px] text-[12px] text-ink-3">{children}</p>
}

/** A section's cards, with the rows still leaving it (ghosts) kept in place for their collapse. */
export function withGhosts(items: DayItem[], ghosts: { item: DayItem; index: number }[]): { item: DayItem; index: number }[] {
  const out = items.map((item, index) => ({ item, index }))
  for (const g of [...ghosts].sort((a, b) => a.index - b.index)) {
    if (out.some((o) => o.item.key === g.item.key)) continue
    out.splice(Math.min(g.index, out.length), 0, g)
  }
  return out
}

